import {
	useGetMyVetterJobsQuery,
	useOnVetterJobUpdatedSubscription,
	useSubscriptionRestored,
	type GetMyVetterJobsQuery,
} from '@codezo/graphql';
import {
	ListFooter,
	PagingModeToggle,
	useListParams,
	usePagedList,
	type RowEstimate,
} from '@codezo/lists';
import { ChevronRight, Inbox, MessageSquare } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchInput } from '../../components/ui/search-input';
import { StatusBadge } from '../../components/ui/status-badge';
import { Tabs } from '../../components/ui/tabs';
import { useQueryParam } from '../../hooks/useQueryParam';
import { JOB_TABS, STAGE_TONE } from '../../lib/job-stage';
import { formatDate } from '../../lib/utils';

type JobRow = GetMyVetterJobsQuery['getMyVetterJobs']['data'][number];

/**
 * One JobListRow, for sizing scroll-mode batches: two lines of text (13px quote name and
 * badge over 12px products, at the default 1.5 line height) inside py-3.5, plus the divider
 * — the same 68px the loading placeholders use.
 */
function jobRows(): RowEstimate {
	return { rowHeight: 69 };
}

/**
 * Every job for the signed-in supplier, newest first, kept live. Tabs filter
 * on the backend's `stage` — "To do" is where the supplier's own turn is, so
 * it's where the page opens. Tab, search, page and paging mode live in the
 * URL, so back returns to the same view.
 */
export function JobsPage() {
	const navigate = useNavigate();
	const [tab, setTab] = useQueryParam('tab', 'todo');
	const {
		search,
		setSearch,
		debouncedSearch,
		page,
		setPage,
		limit,
		mode,
		setMode,
		request,
		loadMore,
		reload,
	} = useListParams({ scrollRows: jobRows });

	const stages = JOB_TABS.find((t) => t.value === tab)?.stages;

	const { data, loading, refetch } = useGetMyVetterJobsQuery({
		variables: {
			input: {
				pagination: request,
				search: debouncedSearch || undefined,
				stages,
			},
		},
		fetchPolicy: 'no-cache',
	});
	const list = usePagedList(
		{ mode, request, limit, setPage, loadMore },
		data?.getMyVetterJobs,
		loading
	);
	/* Rows are kept live by `vetterJobUpdated`: each event is one job's stage
	   and the viewer's own unread count. Held locally so events apply in place;
	   new rows from the server replace the local copy. Pages mode has no rows
	   while a page loads (a fresh empty list each render), so it waits for the
	   answer — the rows on screen stay until then. */
	const [live, setLive] = useState({ rows: list.items, jobs: list.items });
	if (list.items !== live.rows && (mode === 'scroll' || data)) {
		setLive({ rows: list.items, jobs: list.items });
	}
	const jobs = live.jobs;
	const setJobs = (update: (prev: JobRow[]) => JobRow[]) =>
		setLive((l) => ({ ...l, jobs: update(l.jobs) }));
	// What's on screen right now — read by the event handler, which a render ago may be stale.
	const jobsRef = useRef(jobs);
	jobsRef.current = jobs;

	useOnVetterJobUpdatedSubscription({
		onData: ({ data: event }) => {
			const update = event.data?.vetterJobUpdated;
			if (!update) return;
			const inTab = !stages || stages.includes(update.stage);
			const onScreen = jobsRef.current.some((j) => j.id === update.vetterJobId);

			// Withdrawn, or moved to another tab: it leaves this view.
			if (update.removedAt || (onScreen && !inTab)) {
				setJobs((prev) => prev.filter((j) => j.id !== update.vetterJobId));
				return;
			}
			if (onScreen) {
				setJobs((prev) =>
					prev.map((j) =>
						j.id === update.vetterJobId
							? {
									...j,
									stage: update.stage,
									stageLabel: update.stageLabel,
									unreadCommentCount: update.unreadCommentCount,
								}
							: j
					)
				);
				return;
			}
			/* Not on screen and still listed: a new job, or one not loaded yet. It
			   belongs at the top of page one — but the event has no quote name or
			   products to draw it with, so the list is fetched again. Only while
			   page one is in view: a scroll list always starts there, but in pages
			   mode a later page would just shift under the reader. */
			if ((mode === 'scroll' || page === 1) && inTab) reload(refetch);
		},
	});

	// The live stream re-opened after a backend restart — refetch for the gap.
	useSubscriptionRestored(() => reload(refetch), ['OnVetterJobUpdated']);

	// Rebuilds every loaded row in scroll mode, not just the last page fetched.
	// Through a ref so the listeners below aren't re-attached on every render.
	const reloadRef = useRef(() => reload(refetch));
	reloadRef.current = () => reload(refetch);

	/* Stage comes from order data that changes in several places; every path is
	   published, but a refetch on return keeps a missed edge case from lingering. */
	useEffect(() => {
		const onFocus = () => {
			if (!document.hidden) reloadRef.current();
		};
		window.addEventListener('focus', onFocus);
		document.addEventListener('visibilitychange', onFocus);
		return () => {
			window.removeEventListener('focus', onFocus);
			document.removeEventListener('visibilitychange', onFocus);
		};
	}, []);

	return (
		<div className="mx-auto w-full max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			<div className="mb-6">
				<h1 className="mb-2 text-2xl leading-tight font-medium -tracking-[0.02em] text-text-1 sm:text-3xl">
					Jobs
				</h1>
				<p className="text-base text-text-3">
					Price requests, production and shipments from 05 Creation.
				</p>
			</div>

			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={tab}
					onChange={(v) => {
						setTab(v);
						setPage(1);
					}}
					items={JOB_TABS.map(({ value, label }) => ({ value, label }))}
				/>
				<div className="flex items-center gap-2">
					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder="Search quote or product…"
						className="flex-1 lg:flex-none lg:w-[240px]"
					/>
					<PagingModeToggle value={mode} onChange={setMode} />
				</div>
			</div>

			{list.loading && jobs.length === 0 ? (
				<div className="flex flex-col gap-2">
					{[0, 1, 2].map((i) => (
						<div
							key={i}
							className="h-[68px] animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface"
						/>
					))}
				</div>
			) : jobs.length === 0 ? (
				<EmptyJobs tab={tab} searching={!!debouncedSearch} />
			) : (
				<div className="flex flex-col divide-y divide-line-1 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface">
					{jobs.map((job) => (
						<JobListRow
							key={job.id}
							job={job}
							onOpen={() => navigate(`/jobs/${job.id}`)}
						/>
					))}
				</div>
			)}

			<ListFooter list={list} />
		</div>
	);
}

function JobListRow({ job, onOpen }: { job: JobRow; onOpen: () => void }) {
	const names = job.items.map((i) => i.productName).filter((n): n is string => !!n);
	const count = job.items.length;

	return (
		<button
			type="button"
			onClick={onOpen}
			className="flex w-full cursor-pointer items-center gap-4 bg-transparent px-4 py-3.5 text-left transition-colors hover:bg-surface-hover"
		>
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<span className="truncate text-base font-medium text-text-1">
						{job.quoteName}
					</span>
					<StatusBadge
						active
						activeLabel={job.stageLabel}
						activeTone={STAGE_TONE[job.stage]}
					/>
					{job.unreadCommentCount > 0 && (
						<span className="flex shrink-0 items-center gap-1 rounded-full bg-primary px-1.5 py-px text-2xs font-medium text-primary-foreground">
							<MessageSquare size={10} />
							{job.unreadCommentCount} new
						</span>
					)}
				</div>
				<div className="mt-0.5 truncate text-sm text-text-3">
					{count} {count === 1 ? 'product' : 'products'}
					{names.length > 0 && ` · ${names.join(', ')}`}
				</div>
			</div>
			<div className="hidden shrink-0 flex-col items-end gap-0.5 text-sm sm:flex">
				<span className="text-text-2">
					{job.quoteDeadline
						? `Needed by ${formatDate(job.quoteDeadline)}`
						: 'No deadline'}
				</span>
				<span className="text-xs text-text-4">
					{job.createdAt ? `Received ${formatDate(job.createdAt)}` : ''}
					{job.submittedAt && ` · priced ${formatDate(job.submittedAt)}`}
				</span>
			</div>
			<ChevronRight size={16} className="shrink-0 text-text-4" />
		</button>
	);
}

function EmptyJobs({ tab, searching }: { tab: string; searching: boolean }) {
	const text = searching
		? 'No jobs match your search.'
		: tab === 'todo'
			? 'Nothing waiting on you right now.'
			: tab === 'waiting'
				? 'No prices waiting on 05 Creation.'
				: tab === 'done'
					? 'No finished jobs yet.'
					: 'No jobs yet. Price requests from 05 Creation appear here.';

	return (
		<div className="flex flex-col items-center rounded-[var(--r-3)] border border-line-1 bg-surface px-6 py-14 text-center">
			<span className="flex size-10 items-center justify-center rounded-full bg-surface-fill text-text-3">
				<Inbox size={18} />
			</span>
			<div className="mt-4 text-base text-text-2">{text}</div>
		</div>
	);
}
